// src/components/financial/ExpenseBreakdown.js
import React, { useState, useEffect } from 'react';

function ExpenseBreakdown({ expenses, month, year }) {
  const [categoryBreakdown, setCategoryBreakdown] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);

  useEffect(() => {
    processExpenses();
  }, [expenses]);

  const processExpenses = () => {
    const categoryMap = {};
    let total = 0;

    expenses.forEach(expense => {
      const categoryName = expense.expense_categories?.name || 'Uncategorized';
      const categoryIcon = expense.expense_categories?.icon || '📄';
      const amount = parseFloat(expense.amount || 0);
      
      if (!categoryMap[categoryName]) {
        categoryMap[categoryName] = {
          name: categoryName,
          icon: categoryIcon,
          amount: 0,
          count: 0,
          items: []
        };
      }
      
      categoryMap[categoryName].amount += amount;
      categoryMap[categoryName].count += 1;
      categoryMap[categoryName].items.push(expense);
      total += amount;
    });

    const breakdown = Object.values(categoryMap).sort((a, b) => b.amount - a.amount);
    setCategoryBreakdown(breakdown);
    setTotalAmount(total);
  };

  return (
    <div>
      <h3 style={{ marginBottom: '20px' }}>
        Expense Breakdown - {new Date(year, month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
      </h3>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        {/* Category List */}
        <div>
          <h4>By Category</h4>
          {categoryBreakdown.map((category, index) => (
            <div key={index} style={{
              marginBottom: '15px',
              padding: '15px',
              background: '#f8f9fa',
              borderRadius: '8px',
              borderLeft: '4px solid #667eea'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <span style={{ fontSize: '20px', marginRight: '10px' }}>{category.icon}</span>
                  <strong>{category.name}</strong>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                    {category.count} expense{category.count !== 1 ? 's' : ''}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#333' }}>
                    ₹{category.amount.toFixed(2)}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {((category.amount / totalAmount) * 100).toFixed(1)}%
                  </div>
                </div>
              </div>
              
              {/* Progress Bar */}
              <div style={{
                marginTop: '10px',
                height: '8px',
                background: '#e9ecef',
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <div style={{
                  height: '100%',
                  width: `${(category.amount / totalAmount) * 100}%`,
                  background: '#667eea',
                  transition: 'width 0.3s ease'
                }}></div>
              </div>
            </div>
          ))}
        </div>

        {/* Pie Chart Representation */}
        <div>
          <h4>Visual Distribution</h4>
          <div style={{
            padding: '20px',
            background: '#f8f9fa',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>📊</div>
            <h2 style={{ margin: '10px 0', color: '#333' }}>₹{totalAmount.toFixed(2)}</h2>
            <p style={{ color: '#666' }}>Total Expenses</p>
            
            <div style={{ marginTop: '20px', textAlign: 'left' }}>
              {categoryBreakdown.slice(0, 5).map((category, index) => (
                <div key={index} style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '10px'
                }}>
                  <div style={{
                    width: '12px',
                    height: '12px',
                    background: ['#667eea', '#28a745', '#ffc107', '#dc3545', '#17a2b8'][index],
                    borderRadius: '2px',
                    marginRight: '10px'
                  }}></div>
                  <span style={{ fontSize: '14px' }}>
                    {category.icon} {category.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Detailed List */}
      <div style={{ marginTop: '30px' }}>
        <h4>Detailed Expenses</h4>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8f9fa' }}>
                <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Date</th>
                <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Category</th>
                <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Description</th>
                <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Vendor</th>
                <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #dee2e6' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {expenses.map((expense, index) => (
                <tr key={index}>
                  <td style={{ padding: '10px', borderBottom: '1px solid #dee2e6' }}>
                    {new Date(expense.expense_date).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '10px', borderBottom: '1px solid #dee2e6' }}>
                    {expense.expense_categories?.icon} {expense.expense_categories?.name}
                  </td>
                  <td style={{ padding: '10px', borderBottom: '1px solid #dee2e6' }}>
                    {expense.title}
                  </td>
                  <td style={{ padding: '10px', borderBottom: '1px solid #dee2e6' }}>
                    {expense.service_vendors?.name || '-'}
                  </td>
                  <td style={{ padding: '10px', borderBottom: '1px solid #dee2e6', textAlign: 'right' }}>
                    ₹{parseFloat(expense.amount).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default ExpenseBreakdown;